import { Carousel } from '@/components/ui/Carousel'
import { thumbnailPreviewUrl, useThumbnailStyles } from '@/api/endpoints/media'
import { cn } from '@/lib/cn'
import styles from './ThumbnailStyleCarousel.module.scss'

export interface ThumbnailStyleCarouselProps {
  value: string
  onValueChange: (value: string) => void
  /**
   * Optional accent color (hex) forwarded to the preview endpoint so the
   * card matches the user's currently picked color. Today only the
   * ``glow_burst`` style consumes it; other styles render with their
   * built-in accent.
   */
  accentColor?: string
}

/**
 * Carousel of thumbnail-style cards. Each card embeds a Pillow-rendered
 * preview at 9:16 from ``GET /api/v1/media/thumbnail-preview?style=…`` so
 * the picker stays WYSIWYG without paying a Playwright render per card.
 * The catalog comes from ``GET /api/v1/media/thumbnail-styles``, which
 * introspects ``_STYLE_TEMPLATES`` — adding a template on the backend
 * makes it appear here automatically.
 */
export function ThumbnailStyleCarousel({
  value,
  onValueChange,
  accentColor,
}: ThumbnailStyleCarouselProps) {
  const { data: catalog = [], isPending } = useThumbnailStyles()

  if (isPending) {
    return (
      <div className={styles.skeletonRow} aria-busy>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.skeletonCard} />
        ))}
      </div>
    )
  }

  return (
    <Carousel itemWidth={120} gap={10} aria-label="Estilos de thumbnail">
      {catalog.map((opt) => {
        const isActive = opt.key === value
        return (
          <button
            key={opt.key}
            type="button"
            className={cn(styles.card, isActive && styles.active)}
            onClick={() => {
              onValueChange(opt.key)
            }}
            aria-pressed={isActive}
          >
            <div className={styles.preview}>
              <img
                className={styles.previewImg}
                src={thumbnailPreviewUrl(opt.key, accentColor)}
                alt={`Prévia do estilo ${opt.label}`}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </div>
            <div className={styles.name}>{opt.label}</div>
          </button>
        )
      })}
    </Carousel>
  )
}
